import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  viewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { SelectItem } from '@openng/optimus-ui/api';
import { forkJoin, interval, Subscription, take } from 'rxjs';
import {
  LazyLoadFilters,
  PageRequest,
  SearchContainerComponent,
} from 'src/app/core/containers/search-container/search-container.component';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { BreadCrumbBuilderService } from 'src/app/services/builder/breadcrumb-builder.service';
import { getStationLogColumns } from '../../definitions/station-logs-table-definition';
import { StationLog } from 'src/app/model/station-logs/station-log';
import { StationLogApiService } from 'src/app/services/api/station-log-api.service';
import { StationLogBuilderService } from 'src/app/services/builder/station-log-builder.service';
import { StationLogTableService } from 'src/app/services/table/station-log-table.service';
import { TabService } from 'src/app/core/services/tab.service';
import { StationLogReason } from 'src/app/model/station-logs/station-log-reason';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { StationLogFormComponent } from '../../components/station-log-form/station-log-form.component';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { GridLazyLoadEvent } from 'src/app/core/components/base-grid.component';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { GeneralGridComponent } from '../../../core/components/general-grid.component';

@Component({
  selector: 'app-station-logs-search-container',
  templateUrl: './station-logs-search-container.component.html',
  styleUrls: ['./station-logs-search-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BlockUI,
    ButtonDirective,
    ProgressSpinnerComponent,
    GeneralGridComponent,
    StationLogFormComponent,
    TranslatePipe,
  ],
})
export class StationLogsSearchContainerComponent
  extends SearchContainerComponent<StationLog>
  implements OnInit, OnDestroy, AfterViewInit
{
  protected _translateService = inject(TranslateService);
  protected _breadCrumbBuilderService = inject(BreadCrumbBuilderService);
  protected _stationLogApi = inject(StationLogApiService);
  protected _stationLogBuilder = inject(StationLogBuilderService);
  protected _authApi = inject(AuthApiService);
  protected _isRefreshing = false;
  protected _tableService = inject(StationLogTableService);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _tabService = inject(TabService);

  readonly stationLogForm =
    viewChild.required<StationLogFormComponent>('stationLogForm');

  allLogs: StationLog[] = [];
  refreshSubscription?: Subscription;
  availableReasons: SelectItem[] = [];
  formType: string = 'Edit';
  formLoading: boolean = false;
  formVisible: boolean = false;
  canCreate: boolean = false;
  canEdit: boolean = false;
  showSuccessMessage: boolean = true;
  selectedStationLog: StationLog | null = null;

  constructor() {
    super();
  }

  ngOnInit() {
    this.setTableService(this._tableService);
    this.initialize();

    this.canCreate = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.FIELD_OPERATIONS.STATION_LOGS.CREATE_STATION_LOGS,
    ]);
    this.canEdit = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.FIELD_OPERATIONS.STATION_LOGS.EDIT_STATION_LOGS,
    ]);

    const refreshInterval = interval(60000);
    this.refreshSubscription = refreshInterval.subscribe(() => {
      this.refresh(false);
    });
  }

  initialize() {
    this.loadStationReasons();
    const breadCrumb$ = this._translateService.get(
      'OPMONITORING.SCREEN.STATION_LOGS'
    );
    const header$ = this._translateService.get(
      'OPMONITORING.SCREEN.STATION_LOGS'
    );
    forkJoin([header$, breadCrumb$])
      .pipe(take(1))
      .subscribe((mes) => {
        if (this._deviceService.isMobile()) {
          this._breadCrumbBuilderService.resetAndAddBreadCrumb({
            label: mes[1],
            routerLink: null,
          });
        } else {
          this._tabService.updateActiveTabLabel(mes[0]);
        }
        this.screenName = mes[0];
      });
  }

  loadStationReasons() {
    this._stationLogApi
      .getStationLogReasons()
      .pipe(take(1))
      .subscribe({
        next: (data: StationLogReason[]) => {
          this.availableReasons = [
            { label: 'Choose', value: null },
            ...data.map((d) => ({ label: d.reason, value: d.id })),
          ];

          this.columns = getStationLogColumns(this.availableReasons);
          this.loading = false;
        },
        error: (error) => {
          this._logAndMessage.errorLogOnly(error);
          this._logAndMessage.translateToErrorAlert({
            bodyKey: 'OPMONITORING.MESSAGES.ERROR.STATION_LOG_LOAD',
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          });

          this.loading = false;
          this._isRefreshing = false;
        },
      });
  }

  refresh(setLoading = true): void {
    this.lazyLoad({ lazy: this._tableService.getLastLazyLoad()! }, setLoading);
  }

  override handleLazyLoad(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    filters: LazyLoadFilters
  ) {
    const query = this._stationLogBuilder.buildStationLogQuery(filters);
    this.queryNetwork(req, $event, query);
  }

  private queryNetwork(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    query: { [key: string]: any }
  ) {
    req.page += 1;
    const sort = this.buildNetworkSortQuery($event, 'createdAt');

    if (!this._isRefreshing) {
      this._isRefreshing = true;
      this._stationLogApi
        .queryForStationLogs(req.pageSize, req.page, query, sort)
        .pipe(take(1))
        .subscribe({
          next: (response) => {
            this.totalRecords = response.totalRecords;
            this.allLogs = response.items;
            this.elements = [...this.allLogs];
            this._isRefreshing = false;
            this.loading = false;
            if (this.showSuccessMessage) {
              this.showSuccessMessage = false;
              this._logAndMessage.translateToSuccessMessage({
                bodyKey: 'OPMONITORING.MESSAGES.SUCCESS.QUERY_STATION_LOGS',
                headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
              });
            }
          },
          error: (error) => {
            this._isRefreshing = false;
            this.loading = false;
            this._logAndMessage.errorLogOnly(error);
            this._logAndMessage.translateToErrorAlert({
              bodyKey: 'OPMONITORING.MESSAGES.ERROR.STATION_LOG_LOAD',
              headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
            });
          },
        });
    }
  }

  newStationLog() {
    this.formType = 'Create';
    this.stationLogForm().resetEffectiveDate();
    this.stationLogForm().enableForm();
    this.formVisible = true;
  }

  changeViewEvent($event: { data: StationLog }) {
    this.formType = 'Edit';
    this.selectedStationLog = $event.data;

    setTimeout(() => {
      this.stationLogForm().updateFields();
      this.formVisible = true;
    }, 5);
  }

  onFormHide() {
    this.formVisible = false;
  }

  onFormReady() {
    this.formLoading = false;
  }

  onFormSubmit($event: any) {
    this.refresh();
  }

  onStationLogDelete() {
    this.refresh();
  }

  ngAfterViewInit(): void {
    this.applyPreviousFilters();
    this.applyPreviousData();
  }

  ngOnDestroy(): void {
    this.refreshSubscription?.unsubscribe();
  }
}
